import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useArtistAuth } from './ArtistAuthContext';
import { getBugReportStatus } from '../api/adminOperations';
import { resolveAdminPermission } from '../constants/adminPermissionCatalog';

const BugReportingContext = createContext({
  enabled: false,
  loading: true,
  error: null,
  lastUpdatedAt: null,
  lastUpdatedBy: null,
  refreshStatus: () => Promise.resolve(),
});

export function BugReportingProvider({ children }) {
  const { user } = useAuth();
  const { artistUser } = useArtistAuth();
  const [state, setState] = useState({
    enabled: false,
    loading: true,
    error: null,
    lastUpdatedAt: null,
    lastUpdatedBy: null,
  });
  const abortRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await getBugReportStatus({ signal: controller.signal });
      const bugReporting = data?.bugReporting || {};
      setState({
        enabled: Boolean(bugReporting.enabled),
        loading: false,
        error: null,
        lastUpdatedAt: bugReporting.lastUpdatedAt || null,
        lastUpdatedBy: bugReporting.lastUpdatedBy || null,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      const status = Number(error?.status);
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          status === 401 || status === 403
            ? '権限がありません'
            : 'ステータスの取得に失敗しました',
      }));
    } finally {
      abortRef.current = null;
    }
  }, []);

  const isAdminAllowed =
    Boolean(user?.isLoggedIn && user?.userType === 'admin') &&
    String(user?.role || '').toLowerCase() !== 'task_only' &&
    resolveAdminPermission({
      permissionKey: 'NAV_BUGS',
      role: user?.role,
      permissions: user?.permissions,
      isExternalAccount: user?.is_external_account === true,
    });
  const isArtistAllowed = Boolean(artistUser?.isLoggedIn && artistUser?.userType === 'artist');
  const canFetchStatus = isAdminAllowed || isArtistAllowed;

  useEffect(() => {
    if (!canFetchStatus) {
      setState((prev) => ({
        ...prev,
        enabled: false,
        loading: false,
        error: null,
      }));
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return;
    }
    refreshStatus();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [canFetchStatus, refreshStatus]);

  const value = useMemo(
    () => ({
      enabled: state.enabled,
      loading: state.loading,
      error: state.error,
      lastUpdatedAt: state.lastUpdatedAt,
      lastUpdatedBy: state.lastUpdatedBy,
      refreshStatus,
    }),
    [state.enabled, state.loading, state.error, state.lastUpdatedAt, state.lastUpdatedBy, refreshStatus]
  );

  return (
    <BugReportingContext.Provider value={value}>
      {children}
    </BugReportingContext.Provider>
  );
}

export function useBugReporting() {
  return useContext(BugReportingContext);
}
